/**
 * BrochurePage — printable / shareable marketing sheet for BCHPAY.Cash
 * Print double-sided on A4/Letter. Share via URL: bchpay.cash/brochure
 */

import { useEffect } from 'react';
import { QRCodeCanvas } from '@/components/ui/qrcode';

const APP_URL = 'https://bchpay.cash';
const GREEN = '#0AC18E';
const GREEN_DARK = '#088F69';

// ─── Shared atoms ─────────────────────────────────────────────────────────────

function CheckCircle() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="9" fill={GREEN} />
      <path d="M5 9l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <div style={{ width: 3, height: 14, backgroundColor: GREEN, borderRadius: 2 }} />
      <p style={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color: GREEN, margin: 0 }}>
        {children}
      </p>
    </div>
  );
}

// ─── FRONT PANEL ──────────────────────────────────────────────────────────────

function FrontPanel() {
  return (
    <div style={{ width: '210mm', minHeight: '297mm', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Hero header with diagonal cut ── */}
      <div style={{ position: 'relative', background: `linear-gradient(135deg, ${GREEN_DARK} 0%, ${GREEN} 60%, #12D9A4 100%)`, padding: '36px 40px 64px', overflow: 'hidden' }}>

        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.07)' }} />
        <div style={{ position: 'absolute', top: 20, right: 60, width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.07)' }} />
        <div style={{ position: 'absolute', bottom: 20, left: -20, width: 120, height: 120, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.06)' }} />

        {/* Logo row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, position: 'relative' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontWeight: 900, fontSize: 16 }}>BCH</span>
          </div>
          <div>
            <p style={{ color: 'white', fontWeight: 900, fontSize: 22, margin: 0, lineHeight: 1 }}>BCHPAY.Cash</p>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, margin: '4px 0 0' }}>Free Bitcoin Cash POS Terminal</p>
          </div>
        </div>

        {/* Headline */}
        <h1 style={{ color: 'white', fontWeight: 900, fontSize: 34, lineHeight: 1.15, margin: 0, position: 'relative', maxWidth: 340 }}>
          Accept Bitcoin Cash<br />at your business.
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, margin: '10px 0 0', fontWeight: 600 }}>
          No fees. No sign-up. No hardware.
        </p>

        {/* Diagonal white cut */}
        <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 32, backgroundColor: 'white', clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, padding: '8px 40px 24px', display: 'flex', flexDirection: 'column', gap: 22 }}>

        {/* Why BCH */}
        <div>
          <SectionLabel>Why accept Bitcoin Cash?</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              ['Zero processing fees', 'No Stripe, no Square, no 2.9% cut. You keep 100% of every sale.'],
              ['Money goes directly to you', 'Payments land in your own wallet instantly — no holding periods, no chargebacks.'],
              ['Works anywhere in the world', 'No merchant account needed. Sell to anyone, in any country, instantly.'],
              ['No hardware required', 'Runs on your existing phone or tablet. No card reader to rent.'],
              ['Private by design', 'No customer data collected. No tracking. No third party.'],
            ].map(([title, desc]) => (
              <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ marginTop: 2 }}><CheckCircle /></div>
                <p style={{ fontSize: 12.5, color: '#3f3f46', lineHeight: 1.5, margin: 0 }}>
                  <strong style={{ color: '#18181b' }}>{title}</strong> — {desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div>
          <SectionLabel>How it works — 3 steps</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {[
              ['1', 'Open the app', 'Go to bchpay.cash on any phone or tablet.'],
              ['2', 'Enter the amount', 'Type the total or tap items from your menu.'],
              ['3', 'Customer pays', 'They scan the QR with any BCH wallet. Done.'],
            ].map(([step, title, desc]) => (
              <div key={step} style={{ backgroundColor: '#f4fdf9', border: `1px solid ${GREEN}22`, borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: GREEN, color: 'white', fontWeight: 900, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                  {step}
                </div>
                <p style={{ fontSize: 11.5, fontWeight: 700, color: '#18181b', margin: '0 0 4px' }}>{title}</p>
                <p style={{ fontSize: 11, color: '#71717a', lineHeight: 1.4, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features grid */}
        <div>
          <SectionLabel>Everything included — free</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 24px' }}>
            {[
              '⚡ Instant payment detection',
              '🛍️ Built-in product menu & cart',
              '🧾 Digital receipt QR codes',
              '📊 Sales history & analytics',
              '🏷️ Discounts & coupons',
              '💰 Optional tip prompts',
              '🌍 50+ currencies supported',
              '📴 Works fully offline',
              '🔒 Tax / VAT support',
              '📲 Install on any device (PWA)',
            ].map((f) => (
              <p key={f} style={{ fontSize: 11.5, color: '#52525b', margin: 0, lineHeight: 1.6 }}>{f}</p>
            ))}
          </div>
        </div>

        {/* CTA bar */}
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 20, background: `linear-gradient(135deg, ${GREEN_DARK}, ${GREEN})`, borderRadius: 16, padding: '16px 20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 10, padding: 6, flexShrink: 0 }}>
            <QRCodeCanvas value={APP_URL} size={72} level="M" />
          </div>
          <div>
            <p style={{ color: 'white', fontWeight: 900, fontSize: 15, margin: '0 0 4px' }}>Get started in 60 seconds</p>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, margin: '0 0 8px' }}>
              Scan or visit <strong style={{ color: 'white' }}>bchpay.cash</strong>
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              {['No account', 'No fees', 'No hardware'].map((b) => (
                <span key={b} style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 13 }}>✓</span> {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 40px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: '#a1a1aa' }}>bchpay.cash · Free &amp; open source</span>
        <span style={{ fontSize: 10, color: '#a1a1aa' }}>Bitcoin Cash — P2P Electronic Cash</span>
      </div>
    </div>
  );
}

// ─── BACK PANEL ───────────────────────────────────────────────────────────────

function BackPanel() {
  return (
    <div style={{ width: '210mm', minHeight: '297mm', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Dark hero header ── */}
      <div style={{ position: 'relative', backgroundColor: '#18181b', padding: '36px 40px 60px', overflow: 'hidden' }}>

        {/* Decorative accent */}
        <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', backgroundColor: `${GREEN}18` }} />
        <div style={{ position: 'absolute', bottom: 10, right: 80, width: 60, height: 60, borderRadius: '50%', backgroundColor: `${GREEN}10` }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${GREEN_DARK}, ${GREEN}, #12D9A4)` }} />

        {/* Logo row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, position: 'relative' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontWeight: 900, fontSize: 14 }}>BCH</span>
          </div>
          <div>
            <p style={{ color: 'white', fontWeight: 900, fontSize: 18, margin: 0 }}>Pay with Bitcoin Cash</p>
            <p style={{ color: '#71717a', fontSize: 12, margin: '3px 0 0' }}>Fast · Private · Near-zero fees</p>
          </div>
        </div>

        <h1 style={{ color: 'white', fontWeight: 900, fontSize: 28, margin: 0, lineHeight: 1.2, position: 'relative' }}>
          The easiest way to spend{' '}
          <span style={{ color: GREEN }}>BCH</span> in real life.
        </h1>

        {/* Diagonal white cut */}
        <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 28, backgroundColor: 'white', clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, padding: '8px 40px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* How to pay */}
        <div>
          <SectionLabel>How to pay with BCH</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['📱', '1', 'Open your BCH wallet', 'Any wallet works — Electron Cash, Cake Wallet, Paytaca, and more.'],
              ['📷', '2', 'Tap "Send" and scan the QR', "Point your camera at the merchant's screen. The amount fills in automatically."],
              ['✅', '3', 'Confirm and send', 'Review the amount and tap Send. Payment arrives in seconds.'],
            ].map(([icon, step, title, desc]) => (
              <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#f4fdf9', border: `1px solid ${GREEN}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                  {icon}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12.5, fontWeight: 700, color: '#18181b', margin: '0 0 2px' }}>
                    <span style={{ color: GREEN, marginRight: 4 }}>{step}.</span>{title}
                  </p>
                  <p style={{ fontSize: 11.5, color: '#71717a', lineHeight: 1.4, margin: 0 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why BCH for customers */}
        <div>
          <SectionLabel>Why pay with BCH?</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              ['⚡', 'Near-instant', 'Payments confirm in seconds, not minutes.'],
              ['💸', 'Tiny fees', 'Send $100 for fractions of a penny.'],
              ['🔒', 'Your money', 'No bank can freeze or reverse your payment.'],
              ['🌍', 'Works globally', 'Pay anyone, anywhere, anytime.'],
              ['🙅', 'No sign-up', 'No account needed. Just a wallet.'],
              ['🧾', 'Digital receipt', 'Scan the receipt QR code after paying.'],
            ].map(([icon, title, desc]) => (
              <div key={title} style={{ backgroundColor: '#fafafa', border: '1px solid #e4e4e7', borderRadius: 10, padding: '10px 12px' }}>
                <p style={{ fontSize: 18, margin: '0 0 4px' }}>{icon}</p>
                <p style={{ fontSize: 11.5, fontWeight: 700, color: '#18181b', margin: '0 0 2px' }}>{title}</p>
                <p style={{ fontSize: 10.5, color: '#71717a', lineHeight: 1.35, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Get a wallet */}
        <div style={{ backgroundColor: '#f4fdf9', border: `1px solid ${GREEN}33`, borderRadius: 14, padding: '14px 16px' }}>
          <SectionLabel>Get a free BCH wallet</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            {[
              ['Paytaca', 'paytaca.com', 'Best for beginners'],
              ['Cake Wallet', 'cakewallet.com', 'Multi-coin, mobile'],
              ['Electron Cash', 'electroncash.org', 'Desktop & mobile'],
            ].map(([name, url, desc]) => (
              <div key={name} style={{ textAlign: 'center', backgroundColor: 'white', borderRadius: 8, padding: '8px 6px', border: '1px solid #e4e4e7' }}>
                <p style={{ fontSize: 11.5, fontWeight: 700, color: '#18181b', margin: '0 0 2px' }}>{name}</p>
                <p style={{ fontSize: 10.5, fontWeight: 600, color: GREEN, margin: '0 0 2px' }}>{url}</p>
                <p style={{ fontSize: 10, color: '#a1a1aa', margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11, color: '#71717a', textAlign: 'center', margin: 0 }}>
            All wallets are free — download from your app store or their website.
          </p>
        </div>

        {/* What is BCH — dark callout */}
        <div style={{ backgroundColor: '#18181b', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontWeight: 900, fontSize: 12 }}>BCH</span>
          </div>
          <div>
            <p style={{ color: 'white', fontWeight: 700, fontSize: 12.5, margin: '0 0 4px' }}>What is Bitcoin Cash?</p>
            <p style={{ color: '#a1a1aa', fontSize: 11.5, lineHeight: 1.5, margin: 0 }}>
              Bitcoin Cash (BCH) is peer-to-peer electronic cash — fast, cheap, and decentralized.
              No bank, no company, no government controls it. Learn more at{' '}
              <span style={{ color: GREEN, fontWeight: 600 }}>bitcoincash.org</span>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 40px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, backgroundColor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontWeight: 900, fontSize: 8 }}>BCH</span>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#71717a' }}>BCHPAY.Cash</span>
        </div>
        <span style={{ fontSize: 10, color: '#a1a1aa' }}>This merchant accepts Bitcoin Cash</span>
      </div>
    </div>
  );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export function BrochurePage() {
  useEffect(() => {
    document.title = 'BCHPAY.Cash — Brochure';
  }, []);

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div style={{ minHeight: '100vh', backgroundColor: '#d4d4d8' }}>

        {/* Toolbar */}
        <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white', borderBottom: '1px solid #e4e4e7', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontWeight: 900, fontSize: 11 }}>BCH</span>
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: 13, color: '#18181b', margin: 0 }}>BCHPAY.Cash — Brochure</p>
              <p style={{ fontSize: 11, color: '#a1a1aa', margin: 0 }}>Share the link or print double-sided on A4/Letter</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => navigator.clipboard?.writeText(window.location.href).catch(() => {})}
              style={{ padding: '7px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, backgroundColor: '#f4f4f5', color: '#52525b', border: '1px solid #e4e4e7', cursor: 'pointer' }}
            >
              📋 Copy link
            </button>
            <button
              onClick={() => window.print()}
              style={{ padding: '7px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700, backgroundColor: GREEN, color: 'white', border: 'none', cursor: 'pointer' }}
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className="no-print" style={{ maxWidth: '210mm', margin: '20px auto 12px', padding: '0 16px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 14, border: '1px solid #e4e4e7', padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
            <div>
              <p style={{ fontWeight: 600, fontSize: 12.5, color: '#3f3f46', margin: '0 0 4px' }}>Printing tips</p>
              <ul style={{ fontSize: 11.5, color: '#71717a', margin: 0, paddingLeft: 16, lineHeight: 1.7 }}>
                <li>Print <strong>double-sided</strong>, flip on <strong>short edge</strong>, for a front/back handout</li>
                <li>Paper: <strong>A4</strong> or <strong>Letter</strong>, Portrait, margins <strong>None</strong></li>
                <li>Or share the URL: <strong style={{ color: GREEN }}>bchpay.cash/brochure</strong></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Panels */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, paddingBottom: 60, paddingLeft: 16, paddingRight: 16 }}>
          <div style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <FrontPanel />
          </div>
          <div className="no-print" style={{ fontSize: 11, color: '#71717a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            ↑ Front &nbsp;·&nbsp; Back ↓
          </div>
          <div style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <BackPanel />
          </div>
        </div>
      </div>
    </>
  );
}
